import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";
import { CharacterPreset } from "../../models/CharacterPreset";
import { Outfit } from "../../models/Outfit";
import { RootState } from "../Store";

export interface MetadataState {
  /** Outfit catalog pages loaded so far by the pickers, in API order. */
  outfits: Outfit[];
  /** Total outfits in the catalog; undefined until the first page loaded. */
  outfitsTotalCount?: number;
  outfitsLoadedPages: number;
  /**
   * Every outfit known to exist, by name: the ones the open training uses,
   * the loaded catalog pages, and pasted ones that were verified.
   */
  knownOutfits: Record<string, Outfit>;
  characterPresets: CharacterPreset[];
}

const initialMetadataState: MetadataState = {
  outfits: [],
  outfitsTotalCount: undefined,
  outfitsLoadedPages: 0,
  knownOutfits: {},
  characterPresets: [],
};

export const metadataSlice = createSlice({
  name: "metadata",
  initialState: initialMetadataState,
  reducers: {
    /** Resets the outfit state to the outfits the newly opened training uses. */
    initializeOutfits: (state, action: PayloadAction<Outfit[]>) => {
      state.outfits = [];
      state.outfitsTotalCount = undefined;
      state.outfitsLoadedPages = 0;
      state.knownOutfits = Object.fromEntries(
        action.payload.map((outfit) => [outfit.name, outfit])
      );
    },
    addOutfitsPage: (
      state,
      action: PayloadAction<{ page: number; outfits: Outfit[]; totalCount: number }>
    ) => {
      const { page, outfits, totalCount } = action.payload;
      // pages arrive in order; ignore a duplicate response for a loaded page
      if (page !== state.outfitsLoadedPages + 1) return;

      state.outfits.push(...outfits);
      state.outfitsTotalCount = totalCount;
      state.outfitsLoadedPages = page;
      outfits.forEach((outfit) => {
        state.knownOutfits[outfit.name] = outfit;
      });
    },
    addKnownOutfits: (state, action: PayloadAction<Outfit[]>) => {
      action.payload.forEach((outfit) => {
        state.knownOutfits[outfit.name] = outfit;
      });
    },
    initializeCharacterPresets: (
      state,
      action: PayloadAction<CharacterPreset[]>
    ) => {
      state.characterPresets = action.payload;
    },
  },
});

export const {
  initializeOutfits,
  addOutfitsPage,
  addKnownOutfits,
  initializeCharacterPresets,
} = metadataSlice.actions;

export const selectOutfits = (state: RootState) => state.metadata.outfits;
export const selectHasMoreOutfits = (state: RootState) =>
  state.metadata.outfitsTotalCount === undefined ||
  state.metadata.outfits.length < state.metadata.outfitsTotalCount;
export const selectKnownOutfits = (state: RootState) =>
  state.metadata.knownOutfits;
export const selectKnownOutfit = (state: RootState, name: string | undefined) =>
  name ? state.metadata.knownOutfits[name] : undefined;
export const selectKnownOutfitNames = createSelector(
  [selectKnownOutfits],
  (knownOutfits) => new Set(Object.keys(knownOutfits))
);
export const selectCharacterPresets = (state: RootState) =>
  state.metadata.characterPresets;

export default metadataSlice.reducer;
